<script lang="ts">
    import { page, router, useForm } from '@inertiajs/svelte';
    import { toast } from 'svelte-sonner';
    import { Button } from '@/components/ui/button';
    import { CalendarPlus, Pencil, Plus, Trash } from 'lucide-svelte';
    import Modal from '@/components/Modal.svelte';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import { formatDateLocale, formatDateOnly, isWeekendDate } from '@/lib/dateUtils';

    type Phase = {
        id: string;
        sequence: number;
        label: string;
        erect_start_date: string | null;
        strike_end_date: string | null;
        hire_end_date: string | null;
        anticipated_erect_duration: number | null;
        anticipated_strike_duration: number | null;
        anticipated_erect_men: number | null;
        anticipated_strike_men: number | null;
        value: number | null;
        notes: string | null;
        vehicle_assignments_count?: number;
    };

    type Props = {
        recordType: 'lead' | 'job';
        recordId: string;
        phases: Phase[];
        // Mirrors SiteSurveyForm: fires with the fresh record (Lead or Job)
        // pulled from $page.props.flash so kanban-style parents that hydrate
        // out-of-band can refresh without a separate fetch.
        onRecordUpdated?: (updatedRecord: any) => void;
        /** When true, render modals inline instead of portaling to body.
         *  Needed when PhasesList is inside another dialog (StatusChangeModal). */
        inlineModals?: boolean;
    };

    let { recordType, recordId, phases, onRecordUpdated, inlineModals = false }: Props = $props();

    function propagateFlash(): void {
        const flash = ($page as any)?.props?.flash;
        const updated = recordType === 'lead' ? flash?.updatedLead : flash?.updatedJob;
        if (updated && onRecordUpdated) {
            onRecordUpdated(updated);
        }
    }

    let showCreate = $state(false);
    let editing: Phase | null = $state(null);
    let deleting: Phase | null = $state(null);

    const baseUrl = $derived(
        recordType === 'lead'
            ? `/leads/${recordId}/phases`
            : `/jobs/${recordId}/phases`,
    );

    function emptyPhaseValues(): Record<string, any> {
        return {
            label: '',
            erect_start_date: '',
            strike_end_date: '',
            hire_end_date: '',
            anticipated_erect_duration: '',
            anticipated_strike_duration: '',
            anticipated_erect_men: '',
            anticipated_strike_men: '',
            value: '',
            notes: '',
        };
    }

    let createForm = $state(useForm(emptyPhaseValues()));
    let editForm = $state(useForm(emptyPhaseValues()));

    function openCreate() {
        createForm = useForm(emptyPhaseValues());
        showCreate = true;
    }

    function startEdit(phase: Phase) {
        editForm = useForm({
            label: phase.label,
            // <input type="date"> only accepts YYYY-MM-DD; convert the stored
            // datetime down to its date portion.
            erect_start_date: formatDateOnly(phase.erect_start_date),
            strike_end_date: formatDateOnly(phase.strike_end_date),
            hire_end_date: formatDateOnly(phase.hire_end_date),
            anticipated_erect_duration: phase.anticipated_erect_duration,
            anticipated_strike_duration: phase.anticipated_strike_duration,
            anticipated_erect_men: phase.anticipated_erect_men,
            anticipated_strike_men: phase.anticipated_strike_men,
            value: phase.value,
            notes: phase.notes ?? '',
        });
        editing = phase;
    }

    /**
     * Returns true when removing `target` would shift the derived window —
     * i.e. `target` owns the MIN(erect_start_date) or MAX(strike_end_date)
     * across the other active phases. Used to gate the delete confirm.
     */
    function isAnchorPhase(target: Phase): boolean {
        const others = phases.filter((p) => p.id !== target.id);

        const targetErect = target.erect_start_date ? new Date(target.erect_start_date).getTime() : null;
        const targetStrike = target.strike_end_date ? new Date(target.strike_end_date).getTime() : null;

        if (targetErect !== null) {
            const otherMinErect = others
                .map((p) => (p.erect_start_date ? new Date(p.erect_start_date).getTime() : null))
                .filter((v): v is number => v !== null)
                .reduce<number | null>((min, v) => (min === null || v < min ? v : min), null);
            if (otherMinErect === null || targetErect < otherMinErect) {
                return true;
            }
        }

        if (targetStrike !== null) {
            const otherMaxStrike = others
                .map((p) => (p.strike_end_date ? new Date(p.strike_end_date).getTime() : null))
                .filter((v): v is number => v !== null)
                .reduce<number | null>((max, v) => (max === null || v > max ? v : max), null);
            if (otherMaxStrike === null || targetStrike > otherMaxStrike) {
                return true;
            }
        }

        return false;
    }

    function submitCreate(e: Event) {
        e.preventDefault();
        $createForm.post(baseUrl, {
            preserveScroll: true,
            onSuccess: () => {
                showCreate = false;
                toast.success('Phase added');
                propagateFlash();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(message as string);
                }
            },
        });
    }

    function submitEdit(e: Event) {
        e.preventDefault();
        if (!editing) return;
        $editForm.put(`${baseUrl}/${editing.id}`, {
            preserveScroll: true,
            onSuccess: () => {
                editing = null;
                toast.success('Phase updated');
                propagateFlash();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(message as string);
                }
            },
        });
    }

    function requestDelete(phase: Phase) {
        // Anchor phases shift the derived job/lead dates when removed — prompt
        // before firing. Non-anchor phases delete silently.
        if (!isAnchorPhase(phase)) {
            performDelete(phase);
            return;
        }
        deleting = phase;
    }

    function performDelete(phase: Phase) {
        router.delete(`${baseUrl}/${phase.id}`, {
            preserveScroll: true,
            onSuccess: () => {
                deleting = null;
                toast.success('Phase removed');
                propagateFlash();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(message as string);
                }
                deleting = null;
            },
        });
    }

    function confirmDelete() {
        if (!deleting) return;
        performDelete(deleting);
    }

    function addToScheduler(phase: Phase) {
        router.post(`/jobs/${recordId}/phases/${phase.id}/add-to-scheduler`, {}, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`${phase.label} added to scheduler`);
                propagateFlash();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(message as string);
                }
            },
        });
    }
</script>

{#snippet phaseFormFields(form: any)}
    <div class="space-y-4">
        <div class="space-y-1.5">
            <Label for="label">Label <span class="text-destructive">*</span></Label>
            <Input id="label" type="text" bind:value={form.label} required />
            {#if form.errors.label}<p class="text-xs text-destructive">{form.errors.label}</p>{/if}
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-1.5">
                <Label for="erect_start_date">Erect start date</Label>
                <Input id="erect_start_date" type="date" bind:value={form.erect_start_date} />
                {#if form.errors.erect_start_date}<p class="text-xs text-destructive">{form.errors.erect_start_date}</p>{/if}
                {#if isWeekendDate(form.erect_start_date)}
                    <p class="text-xs" style="color: var(--warning);" role="alert">
                        Heads up — this falls on a weekend. You can still save it.
                    </p>
                {/if}
            </div>
            <div class="space-y-1.5">
                <Label for="strike_end_date">Strike end date</Label>
                <Input id="strike_end_date" type="date" bind:value={form.strike_end_date} />
                {#if form.errors.strike_end_date}<p class="text-xs text-destructive">{form.errors.strike_end_date}</p>{/if}
                {#if isWeekendDate(form.strike_end_date)}
                    <p class="text-xs" style="color: var(--warning);" role="alert">
                        Heads up — this falls on a weekend. You can still save it.
                    </p>
                {/if}
            </div>
        </div>

        <div class="space-y-1.5">
            <Label for="hire_end_date">Hire end date <span class="text-xs text-muted-foreground font-normal">(optional — leave blank if same as strike)</span></Label>
            <Input id="hire_end_date" type="date" bind:value={form.hire_end_date}
                onfocus={() => { if (!form.hire_end_date && form.strike_end_date) form.hire_end_date = form.strike_end_date; }}
            />
            {#if form.errors.hire_end_date}<p class="text-xs text-destructive">{form.errors.hire_end_date}</p>{/if}
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-1.5">
                <Label for="anticipated_erect_duration">Anticipated erect duration (days)</Label>
                <Input id="anticipated_erect_duration" type="number" step="0.01" min="0" bind:value={form.anticipated_erect_duration} />
                {#if form.errors.anticipated_erect_duration}<p class="text-xs text-destructive">{form.errors.anticipated_erect_duration}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="anticipated_strike_duration">Anticipated strike duration (days)</Label>
                <Input id="anticipated_strike_duration" type="number" step="0.01" min="0" bind:value={form.anticipated_strike_duration} />
                {#if form.errors.anticipated_strike_duration}<p class="text-xs text-destructive">{form.errors.anticipated_strike_duration}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="anticipated_erect_men">Anticipated erect men</Label>
                <Input id="anticipated_erect_men" type="number" step="1" min="0" bind:value={form.anticipated_erect_men} />
                {#if form.errors.anticipated_erect_men}<p class="text-xs text-destructive">{form.errors.anticipated_erect_men}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="anticipated_strike_men">Anticipated strike men</Label>
                <Input id="anticipated_strike_men" type="number" step="1" min="0" bind:value={form.anticipated_strike_men} />
                {#if form.errors.anticipated_strike_men}<p class="text-xs text-destructive">{form.errors.anticipated_strike_men}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="value">Value</Label>
                <Input id="value" type="number" step="0.01" min="0" bind:value={form.value} />
                {#if form.errors.value}<p class="text-xs text-destructive">{form.errors.value}</p>{/if}
            </div>
        </div>

        <div class="space-y-1.5">
            <Label for="notes">Notes</Label>
            <textarea id="notes" rows="3" class="w-full border rounded-md px-3 py-2 text-sm" bind:value={form.notes}></textarea>
        </div>
    </div>
{/snippet}

<section id="section-phases" class="bg-card border rounded-lg p-4 space-y-4">
    <div class="flex items-center justify-between">
        <div>
            <h2 class="text-lg font-semibold">Phases</h2>
            <p class="text-sm text-muted-foreground">
                Phases are the source of truth for the job's start and end dates.
            </p>
        </div>
        <Button
            size="sm"
            variant="outline"
            onclick={openCreate}
            class="flex items-center gap-x-2"
        >
            <Plus class="h-4 w-4" />
            Add Phase
        </Button>
    </div>

    {#if phases.length === 0}
        <div class="text-sm text-muted-foreground border border-dashed rounded p-6 text-center">
            No phases yet. Add one to set the job's dates.
        </div>
    {:else}
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead class="text-left text-muted-foreground border-b">
                    <tr>
                        <th class="py-2 pr-2">#</th>
                        <th class="py-2 pr-2">Label</th>
                        <th class="py-2 pr-2">Erect start</th>
                        <th class="py-2 pr-2">Strike end</th>
                        <th class="py-2 pr-2">Hire end</th>
                        <th class="py-2 pr-2">Erect / Strike days</th>
                        <th class="py-2 pr-2">Erect / Strike men</th>
                        <th class="py-2 pr-2">Value</th>
                        <th class="py-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {#each phases as phase (phase.id)}
                        <tr class="border-b last:border-0">
                            <td class="py-2 pr-2">{phase.sequence}</td>
                            <td class="py-2 pr-2">{phase.label}</td>
                            <td class="py-2 pr-2">{phase.erect_start_date ? formatDateLocale(phase.erect_start_date, true) : '—'}</td>
                            <td class="py-2 pr-2">{phase.strike_end_date ? formatDateLocale(phase.strike_end_date, true) : '—'}</td>
                            <td class="py-2 pr-2">{phase.hire_end_date ? formatDateLocale(phase.hire_end_date, true) : '—'}</td>
                            <td class="py-2 pr-2">{phase.anticipated_erect_duration ?? '—'} / {phase.anticipated_strike_duration ?? '—'}</td>
                            <td class="py-2 pr-2">{phase.anticipated_erect_men ?? '—'} / {phase.anticipated_strike_men ?? '—'}</td>
                            <td class="py-2 pr-2">{phase.value != null ? `£${Number(phase.value).toFixed(2)}` : '—'}</td>
                            <td class="py-2 text-right whitespace-nowrap">
                                {#if recordType === 'job' && (phase.vehicle_assignments_count ?? 0) === 0}
                                    <Button size="sm" variant="ghost" onclick={() => addToScheduler(phase)} title="Add to scheduler">
                                        <CalendarPlus class="h-4 w-4 text-primary" />
                                    </Button>
                                {/if}
                                <Button size="sm" variant="ghost" onclick={() => startEdit(phase)}>
                                    <Pencil class="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onclick={() => requestDelete(phase)}>
                                    <Trash class="h-4 w-4 text-destructive" />
                                </Button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</section>

{#if showCreate}
    {#if inlineModals}
        <div class="border rounded-lg p-4 mt-2 bg-muted/50 space-y-4">
            <h3 class="text-sm font-semibold">Add Phase</h3>
            <form onsubmit={submitCreate} class="space-y-4">
                {@render phaseFormFields($createForm)}
                <div class="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onclick={() => (showCreate = false)}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {$createForm.processing ? 'Saving…' : 'Save phase'}
                    </Button>
                </div>
            </form>
        </div>
    {:else}
        <Modal title="Add Phase" onClose={() => (showCreate = false)}>
            <form onsubmit={submitCreate} class="space-y-4">
                {@render phaseFormFields($createForm)}
                <div class="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onclick={() => (showCreate = false)}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {$createForm.processing ? 'Saving…' : 'Save phase'}
                    </Button>
                </div>
            </form>
        </Modal>
    {/if}
{/if}

{#if editing}
    {#if inlineModals}
        <div class="border rounded-lg p-4 mt-2 bg-muted/50 space-y-4">
            <h3 class="text-sm font-semibold">Edit Phase</h3>
            <form onsubmit={submitEdit} class="space-y-4">
                {@render phaseFormFields($editForm)}
                <div class="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onclick={() => (editing = null)}>Cancel</Button>
                    <Button type="submit" disabled={$editForm.processing}>
                        {$editForm.processing ? 'Saving…' : 'Save changes'}
                    </Button>
                </div>
            </form>
        </div>
    {:else}
        <Modal title="Edit Phase" onClose={() => (editing = null)}>
            <form onsubmit={submitEdit} class="space-y-4">
                {@render phaseFormFields($editForm)}
                <div class="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onclick={() => (editing = null)}>Cancel</Button>
                    <Button type="submit" disabled={$editForm.processing}>
                        {$editForm.processing ? 'Saving…' : 'Save changes'}
                    </Button>
                </div>
            </form>
        </Modal>
    {/if}
{/if}

{#if deleting}
    {#if inlineModals}
        <div class="border rounded-lg p-4 mt-2 bg-destructive/10 space-y-4">
            <h3 class="text-sm font-semibold">Remove anchor phase</h3>
            <p class="text-sm">
                Removing <strong>{deleting.label}</strong> will shift the job's start or end date —
                it owns the earliest erect or latest strike across the remaining phases.
            </p>
            <p class="text-sm text-muted-foreground">
                Any associated vehicle and employee assignments will also be soft-deleted.
            </p>
            <div class="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onclick={() => (deleting = null)}>Cancel</Button>
                <Button type="button" variant="destructive" onclick={confirmDelete}>Remove phase</Button>
            </div>
        </div>
    {:else}
        <Modal title="Remove anchor phase" onClose={() => (deleting = null)}>
            <p class="text-sm">
                Removing <strong>{deleting.label}</strong> will shift the job's start or end date —
                it owns the earliest erect or latest strike across the remaining phases.
            </p>
            <p class="text-sm mt-2 text-muted-foreground">
                Any associated vehicle and employee assignments will also be soft-deleted.
            </p>
            <div class="flex justify-end gap-2 pt-4">
                <Button type="button" variant="ghost" onclick={() => (deleting = null)}>Cancel</Button>
                <Button type="button" variant="destructive" onclick={confirmDelete}>Remove phase</Button>
            </div>
        </Modal>
    {/if}
{/if}